import { LineChart, LineChartSeries } from "@mantine/charts";
import {
  Box,
  Group,
  Menu,
  Select,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { TimeframeInput } from "../TimeframeInput";
import { ConditionsTooltipContent } from "../TooltipContent";
import { PerformanceChartTooltip } from "./components";
import {
  getColorFromIndex,
  getColorFromName,
  getDataRange,
  getDateFormatter,
  getUniqueTicks,
} from "./utils";

import lockerIcon from "@/assets/icons/locker.svg";
import { ChoosePlanModal } from "@/components/widgets/PlanModals";
import Popover from "@/components/widgets/Popover/Popover";
import { defaultCurrency } from "@/lib/currency";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { formatCurrency } from "@/utils/formatEuro";

import type { Currency } from "@/types/currency";
import type { DatesRange } from "@/types/entity";
import type { PerformanceChart as IPerformanceChart } from "@/types/performance";
import type { LineChartProps } from "@mantine/charts";
import type { ComboboxData, MantineStyleProps } from "@mantine/core";
import type { FC, PropsWithChildren } from "react";
import type { DropdownItem } from "../Dropdown/types";
import type { TimeframeRange } from "../TimeframeInput/types";

export type ChartProps = {
  curveType?: LineChartProps["curveType"];
  h?: MantineStyleProps["h"];
  dataKey?: string;
  valueFormatter?: (value: number) => string;
  withDots?: boolean;
  withLegend?: boolean;
};

export type PerformanceChartProps = PropsWithChildren<{
  benchmarks?: IPerformanceChart[];
  chartProps?: ChartProps;
  defaultBenchmark?: string;
  /** When true, treat `defaultBenchmark === "__allCars__"` as a special case and
   * replace the "__all__" label with "All cars". Also enables compare-specific behavior
   * (each car gets its own series/toggle) when the chart is used inside compare widgets.
   */
  compareMode?: boolean;
  hideBenchmarkSelector?: boolean;
  hiddenBenchmarks?: string[];
  loading?: boolean;
  onTimeframeChange?: (range?: DatesRange) => void;
  performance?: IPerformanceChart;
  tooltipFormatter?: (value: any) => string;
  /** When false, hide the chart tooltip (visible by default) */
  showTooltip?: boolean;
  /** When false, hide the small "Conditions" help tooltip next to the selector */
  showConditionsTooltip?: boolean;
  restrictKpisByPlan?: boolean;
  /** When true (default) show the `performance` series together with a selected benchmark. Set to false to show only the benchmark when a single condition is selected. */
  showPerformanceWhenBenchmarkSelected?: boolean;
  /** When true, show the locker icon + unlock text next to the performance series in the legend */
  showLockerInLegend?: boolean;
}> &
  MantineStyleProps;

type PerformanceChartComponent = FC<PerformanceChartProps> & {
  Tooltip: typeof PerformanceChartTooltip;
};

const Component: PerformanceChartComponent = ({
  benchmarks = [],
  chartProps = {},
  children,
  defaultBenchmark,
  hideBenchmarkSelector = false,
  hiddenBenchmarks = [],
  restrictKpisByPlan = false,
  loading = false,
  onTimeframeChange,
  performance,
  tooltipFormatter,
  showTooltip = true,
  showConditionsTooltip = true,
  showPerformanceWhenBenchmarkSelected = true,
  compareMode = false,
  showLockerInLegend = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();
  const useAllCarsLabel = compareMode && defaultBenchmark === "__allCars__";
  const allLabel = useAllCarsLabel
    ? t("components.PerformanceChart.benchmarks.allCars", "All cars")
    : t("components.PerformanceChart.benchmarks.__all__", "All conditions");
  const [currentBenchmark, setBenchmark] = useState<string | undefined>(undefined);
  const [popoverOpened, setPopoverOpened] = useState(false);

  const { data: plans } = usePlansUser();

  const kpisDisabled = restrictKpisByPlan && plans?.config?.garage_risk_kpis === false;

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const chartColor = isDark ? "#80c9df" : "primary";

  const isMobile = useMatches({ base: true, md: false });

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const defaultChartProps: ChartProps = {
    h: 300,
    valueFormatter: (value) =>
      value >= 0
        ? formatCurrency(value, currency.code, {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 0,
          })
        : "",
    withLegend: true,
  };

  // Tooltip values are never rounded — always use standard notation regardless of localStorage.
  const tooltipValueFormatter = (value: number) =>
    value >= 0
      ? formatCurrency(value, currency.code, {
          notation: "standard",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : "";

  const lineChartProps = {
    ...defaultChartProps,
    ...chartProps,
  };

  // If KPI filtering is enabled, and KPIs are disabled for the plan,
  // hide individual conditions and only expose the overall/average view (__all__).
  // If KPI filtering is disabled (prop=false), keep original behavior.
  let dropdownBenchmarks: DropdownItem[] = [];
  const hiddenSet = new Set(hiddenBenchmarks || []);
  const filteredBenchmarks = benchmarks.filter((b) => !hiddenSet.has(b.name));
  const visibleName = (n?: string | number) => String(n ?? "").split("\u200B")[0];
  if (restrictKpisByPlan) {
    if (kpisDisabled) {
      dropdownBenchmarks = [
        {
          label: allLabel,
          value: "__all__",
        },
      ];
    } else {
      dropdownBenchmarks = filteredBenchmarks.map(({ name }) => {
        const v = visibleName(name);
        return {
          label: t(`components.PerformanceChart.benchmarks.${v}`, v),
          value: name,
        };
      });
      // In compare mode include the performance series (first car) as a selectable option
      if (compareMode && performance && performance.name && !hiddenSet.has(performance.name)) {
        if (!dropdownBenchmarks.find((d) => d.value === performance.name)) {
          const v = visibleName(performance.name);
          dropdownBenchmarks.unshift({
            label: t(`components.PerformanceChart.benchmarks.${v}`, v),
            value: performance.name,
          });
        }
      }
      if (filteredBenchmarks.length > 0 && !dropdownBenchmarks.find((d) => d.value === "__all__")) {
        dropdownBenchmarks.push({ label: allLabel, value: "__all__" });
      }
    }
  } else {
    dropdownBenchmarks = filteredBenchmarks.map(({ name }) => {
      const v = visibleName(name);
      return { label: t(`components.PerformanceChart.benchmarks.${v}`, v), value: name };
    });
    if (compareMode && performance && performance.name && !hiddenSet.has(performance.name)) {
      if (!dropdownBenchmarks.find((d) => d.value === performance.name)) {
        const v = visibleName(performance.name);
        dropdownBenchmarks.unshift({
          label: t(`components.PerformanceChart.benchmarks.${v}`, v),
          value: performance.name,
        });
      }
    }
    if (filteredBenchmarks.length > 0 && !dropdownBenchmarks.find((d) => d.value === "__all__")) {
      dropdownBenchmarks.push({ label: allLabel, value: "__all__" });
    }
  }

  // When the new behavior is enabled, always show the select (unless explicitly hidden).
  // Otherwise revert to previous behavior (show only if more than one option).
  const hasDropdown = restrictKpisByPlan
    ? !hideBenchmarkSelector && filteredBenchmarks.length > 0
    : dropdownBenchmarks.length > 1 && !hideBenchmarkSelector && filteredBenchmarks.length > 0;

  const visibleBenchmarks = restrictKpisByPlan
    ? kpisDisabled
      ? []
      : filteredBenchmarks
    : filteredBenchmarks;

  const { chart, range, series } = useMemo(() => {
    let chart: Record<string, any>[] = [];
    let series: LineChartSeries[] = [];

    if (performance) {
      // If performance has no data but there are visible benchmarks, prefer rendering benchmarks
      const perfHasData = (performance.data?.length ?? 0) > 0;
      if (!perfHasData && visibleBenchmarks.length > 0) {
        // Build chart from benchmarks (use the longest benchmark as reference)
        const reference = visibleBenchmarks.reduce((prev, cur) =>
          cur.data.length > prev.data.length ? cur : prev
        );

        chart = reference.data.map(({ date }, index) => {
          const item: Record<string, number | string> = { date };
          visibleBenchmarks.forEach((benchmark) => {
            item[benchmark.name] = benchmark.data[index]?.value ?? 0;
          });
          return item;
        });

        series = visibleBenchmarks.map((benchmark) => {
          const v = visibleName(benchmark.name);
          return {
            color: getBenchmarkColor(benchmark.name),
            name: benchmark.name,
            label: t(`components.PerformanceChart.benchmarks.${v}`, v),
          };
        }) as LineChartSeries[];
      } else {
        if (currentBenchmark === "__all__") {
          chart = performance.data.map(({ date, value }, index) => {
            const item: Record<string, number | string> = { date, [performance.name]: value };
            visibleBenchmarks.forEach((benchmark) => {
              item[benchmark.name] = benchmark.data[index]?.value ?? 0;
            });
            return item;
          });

          series = [
            ...visibleBenchmarks.map((benchmark) => {
              const v = visibleName(benchmark.name);
              return {
                color: getBenchmarkColor(benchmark.name),
                name: benchmark.name,
                label: t(`components.PerformanceChart.benchmarks.${v}`, v),
              };
            }),
            {
              color: "primary.5",
              name: performance.name,
              label: t(
                `components.PerformanceChart.benchmarks.${visibleName(performance.name)}`,
                visibleName(performance.name)
              ),
            },
          ] as LineChartSeries[];
        } else {
          // If in compareMode, selecting a specific car should show only that car.
          if (compareMode) {
            // Selecting the performance series (first car)
            if (currentBenchmark === performance.name) {
              chart = (performance.data || []).map(({ date, value }) => ({
                date,
                [performance.name]: value,
              }));

              series = [
                {
                  color: chartColor,
                  name: performance.name,
                  label: t(
                    `components.PerformanceChart.benchmarks.${visibleName(performance.name)}`,
                    visibleName(performance.name)
                  ),
                },
              ] as LineChartSeries[];
            } else {
              // Selecting another benchmark (other cars)
              const benchmark = visibleBenchmarks.find(({ name }) => name === currentBenchmark);
              const reference = benchmark || visibleBenchmarks[0] || { data: [] as any[] };

              chart = (reference.data || []).map(({ date }, index) => ({
                date,
                [reference.name]: benchmark?.data[index]?.value ?? 0,
              }));

              series = [
                {
                  color: getBenchmarkColor(reference.name),
                  name: reference.name,
                  label: t(
                    `components.PerformanceChart.benchmarks.${visibleName(reference.name)}`,
                    visibleName(reference.name)
                  ),
                },
              ] as LineChartSeries[];
            }
          } else {
            const benchmark = visibleBenchmarks.find(({ name }) => name === currentBenchmark);
            chart = performance.data.map(({ date, value }, index) => ({
              date,
              [performance.name]: value,
              [currentBenchmark || "none"]: benchmark?.data[index]?.value ?? 0,
            }));

            series = [
              currentBenchmark && {
                color: getBenchmarkColor(currentBenchmark),
                name: currentBenchmark,
                label: t(
                  `components.PerformanceChart.benchmarks.${visibleName(currentBenchmark)}`,
                  visibleName(currentBenchmark)
                ),
              },
              showPerformanceWhenBenchmarkSelected && {
                color: chartColor,
                name: performance.name,
                label: t(
                  `components.PerformanceChart.benchmarks.${visibleName(performance.name)}`,
                  visibleName(performance.name)
                ),
              },
            ].filter(Boolean) as LineChartSeries[];
          }
        }
      }
    }

    // Strip out any hidden benchmark keys from the chart data and series
    const cleanedChart = chart.map((item) => {
      const copy: Record<string, any> = { ...item };
      Object.keys(copy).forEach((key) => {
        if (hiddenSet.has(key)) delete copy[key];
      });
      return copy;
    });

    const cleanedSeries = series.filter((s) => !hiddenSet.has(String(s.name)));

    return { chart: cleanedChart, range: getDataRange(cleanedChart), series: cleanedSeries };
  }, [performance, visibleBenchmarks, currentBenchmark, isDark, compareMode]);

  const tickFormatter = getDateFormatter(range);
  const ticks = getUniqueTicks(
    chart.map((item) => item.date),
    tickFormatter
  );

  const hasItems =
    (performance?.data?.length ?? 0) > 0 || visibleBenchmarks.some((b) => b.data?.length > 0);

  // Track whether the original (initial) performance data contains items.
  // We only want to show the TimeframeInput when the initial dataset is non-empty.
  // Use state (not ref) so the component re-renders when data arrives.
  const [initialPerformanceHasData, setInitialPerformanceHasData] = useState<boolean | null>(null);

  useEffect(() => {
    // Initialize once. Consider the initial dataset non-empty when either the
    // `performance` payload contains data OR any visible benchmark contains data.
    if (initialPerformanceHasData !== null) return;

    const performanceLoaded = performance != null;
    if (!performanceLoaded && visibleBenchmarks.length === 0) return;

    const initialHasData =
      (performance?.data?.length ?? 0) > 0 ||
      visibleBenchmarks.some((b) => (b.data?.length ?? 0) > 0);

    setInitialPerformanceHasData(initialHasData);
  }, [performance, visibleBenchmarks, initialPerformanceHasData]);

  // Show the TimeframeInput only if a handler is provided AND the initial
  // performance data (before any timeframe filtering) contained items.
  const showTimeframeInput = !!onTimeframeChange && (initialPerformanceHasData ?? false);

  function getBenchmarkColor(name: string) {
    const color = getColorFromName(name);
    if (!color) {
      const index = visibleBenchmarks.findIndex((b) => b.name === name);
      return getColorFromIndex(index);
    }
    return color;
  }

  function handleTimeframeChange(dates?: TimeframeRange) {
    if (dates?.[0] && dates?.[1]) {
      onTimeframeChange?.({ from: dates[0] as string, to: dates[1] as string });
    } else {
      onTimeframeChange?.();
    }
  }

  function setDefaultBenchmark() {
    // If a defaultBenchmark prop is provided and valid, use it.
    if (!currentBenchmark) {
      // Special-case: when used in compare mode, allow `__allCars__` as a default
      // which maps to the internal `__all__` option and shows "All cars" label.
      if (compareMode && defaultBenchmark === "__allCars__") {
        setBenchmark("__all__");
        return;
      }
      if (defaultBenchmark && dropdownBenchmarks.find((d) => d.value === defaultBenchmark)) {
        setBenchmark(defaultBenchmark);
        return;
      }

      // Otherwise default to the first dropdown option (index 0) when available
      if (dropdownBenchmarks.length > 0) {
        setBenchmark(dropdownBenchmarks[0].value);
      }
    }
  }

  // Show the group menu if there's a dropdown or if timeframe changes are enabled.
  // Keep the timeframe input visible when `onTimeframeChange` exists,
  // even if there are no data points for the current selection.
  const hasGroupMenu =
    !!onTimeframeChange ||
    ((hasDropdown || false) &&
      ((performance?.data?.length ?? 0) > 0 || visibleBenchmarks.some((b) => b.data?.length > 0)));

  useEffect(() => {
    setDefaultBenchmark();
  }, [benchmarks, restrictKpisByPlan, defaultBenchmark, visibleBenchmarks]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayedValue = useMemo(() => {
    if (currentBenchmark) {
      const item = dropdownBenchmarks.find((b) => b.value === currentBenchmark);
      if (item) return item.label;
    }
    return allLabel;
  }, [currentBenchmark, dropdownBenchmarks, t]);

  // Select value must be the option `value`, not the label. When no benchmark
  // is selected, fall back to `__all__` if available so the Select shows correctly.
  const selectValue =
    currentBenchmark ??
    (dropdownBenchmarks.find((d) => d.value === "__all__") ? "__all__" : undefined);

  // Custom legend renderer to add the locker icon next to the series label
  const CustomLegend = ({ payload }: any) => {
    if (!payload || !Array.isArray(payload)) return null;

    return (
      <Group gap="xl" align="center" style={{ paddingTop: 8 }}>
        {payload.map((entry: any) => {
          const entryValue = entry?.value ?? entry?.payload?.value ?? entry?.payload?.name;
          const labelText = entry?.payload?.payload?.label ?? entry?.value ?? entryValue;
          const showLocker =
            !!showLockerInLegend &&
            !!performance &&
            String(entryValue) === String(performance.name);

          // Resolve CSS color: if entry.color is a hex use it, otherwise map token to CSS variable
          const rawColor = entry?.color;
          let cssColor = "var(--mantine-color-primary-5)";
          if (rawColor) {
            const s = String(rawColor);
            if (s.startsWith("#")) {
              cssColor = s;
            } else if (s.startsWith("var(")) {
              cssColor = s;
            } else {
              // If token contains a shade (e.g. `primary.5`) convert dot to dash
              // otherwise default to the `-5` shade to ensure a visible color.
              if (s.includes(".")) {
                const token = s.replace(/\./g, "-");
                cssColor = `var(--mantine-color-${token})`;
              } else {
                cssColor = `var(--mantine-color-${s}-5)`;
              }
            }
          } else if (performance && String(entryValue) === String(performance.name)) {
            // If no explicit color provided, prefer the performance series' color so
            // the average dot is visible even when it's the only series.
            const fallback = getBenchmarkColor(performance.name);
            const fb = String(fallback);
            cssColor = fb.startsWith("#") ? fb : `var(--mantine-color-${fb.replace(/\./g, "-")})`;
          }

          return (
            <Group key={String(entryValue)} gap="sm" style={{ alignItems: "center" }}>
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: cssColor,
                }}
              />
              <Text
                size="xs"
                style={{
                  color: isDark ? "#fff" : "var(--mantine-color-secondary-6)",
                  textTransform: "none",
                }}
              >
                {labelText}
              </Text>
              {showLocker && (
                <Group
                  gap={6}
                  align="center"
                  style={{ marginLeft: 6, cursor: "pointer" }}
                  onClick={() => openPlanModal()}
                >
                  <Box
                    component="img"
                    src={lockerIcon as unknown as string}
                    alt={t("plans.contentLocker.upgradeToAccess")}
                    style={{ width: 22, height: 22, objectFit: "contain", display: "inline-block" }}
                  />
                  <Text size="xs" c="dimmed">
                    {t("components.PerformanceChart.unlockOtherAnalysis", "Unlock other analysis")}
                  </Text>
                </Group>
              )}
            </Group>
          );
        })}
      </Group>
    );
  };

  return (
    <Skeleton visible={loading}>
      <Stack {...props} pos="relative">
        {hasGroupMenu && (
          <Group justify={hasDropdown ? "space-between" : "flex-end"}>
            {hasDropdown &&
              (isMobile ? (
                <>
                  <Popover
                    title={t("components.PerformanceChart.selectCondition")}
                    actionInPending={false}
                    popoverOpened={popoverOpened}
                    setPopoverOpened={setPopoverOpened}
                    target={
                      <Tooltip
                        label={t("plans.contentLocker.upgradeToAccess")}
                        disabled={!kpisDisabled || !showConditionsTooltip}
                        withArrow
                        events={{ hover: true, focus: true, touch: true }}
                        position="top"
                        offset={-8}
                        maw={300}
                        openDelay={100}
                        multiline
                      >
                        <Box
                          style={{ cursor: "pointer" }}
                          onClick={() => setPopoverOpened((prev) => !prev)}
                        >
                          <Select
                            disabled={kpisDisabled}
                            label={t("components.PerformanceChart.conditions")}
                            value={selectValue}
                            data={dropdownBenchmarks as ComboboxData}
                            readOnly
                            w={200}
                            placeholder={displayedValue}
                          />
                        </Box>
                      </Tooltip>
                    }
                  >
                    <Menu>
                      {dropdownBenchmarks.map((item) => (
                        <Menu.Item
                          key={`benchmark-${item.value}`}
                          onClick={() => {
                            setBenchmark(item.value);
                            setPopoverOpened(false);
                          }}
                          style={{
                            cursor: "pointer",
                            padding: "var(--mantine-spacing-sm)",
                            borderRadius: "var(--mantine-radius-default)",
                            backgroundColor:
                              item.value === currentBenchmark
                                ? isDark
                                  ? "var(--mantine-color-dark-4)"
                                  : "var(--mantine-color-gray-0)"
                                : "transparent",
                            "&:hover": {
                              backgroundColor: isDark
                                ? "var(--mantine-color-dark-4)"
                                : "var(--mantine-color-gray-0)",
                            },
                          }}
                        >
                          <Text size="sm">{item.label}</Text>
                        </Menu.Item>
                      ))}
                    </Menu>
                  </Popover>
                </>
              ) : (
                <>
                  <Tooltip
                    label={t("plans.contentLocker.upgradeToAccess")}
                    disabled={!kpisDisabled || !showConditionsTooltip}
                    withArrow
                    events={{ hover: true, focus: true, touch: true }}
                    position="top"
                    offset={-8}
                    maw={300}
                    openDelay={100}
                    multiline
                  >
                    <Box>
                      <Dropdown
                        data={dropdownBenchmarks}
                        disabled={kpisDisabled}
                        inputProps={{ miw: 200 }}
                        label={
                          showConditionsTooltip
                            ? t("components.PerformanceChart.conditions")
                            : undefined
                        }
                        tooltip={showConditionsTooltip ? <ConditionsTooltipContent /> : undefined}
                        value={selectValue}
                        variant="default"
                        onChange={(value) => value && setBenchmark(value)}
                      />
                    </Box>
                  </Tooltip>
                </>
              ))}
            {showTimeframeInput && (
              <TimeframeInput
                label={t("components.PerformanceChart.timeframe")}
                defaultValue="all"
                onChange={handleTimeframeChange}
                inputProps={{
                  placeholder: t("components.PerformanceChart.timeframe_placeholder"),
                  miw: 200,
                }}
                maxDate={new Date()}
                type="month"
                valueFormat="MMM YYYY"
              />
            )}
          </Group>
        )}
        {children}
        {hasItems ? (
          <>
            <style>{`.car-performance-chart .recharts-line path, .car-performance-chart .recharts-curve { stroke-opacity: 0.75; } .car-performance-chart .recharts-area .recharts-curve { fill-opacity: 0.08; }`}</style>
            <LineChart
              data={chart}
              className="car-performance-chart"
              dataKey="date"
              dotProps={{ r: chart.length === 1 ? 2 : 0 }}
              series={series}
              strokeDasharray="0"
              tickLine="none"
              xAxisProps={{
                tickFormatter,
                ticks,
              }}
              tooltipProps={{
                content: ({ label, payload }) => {
                  const visible = visibleName(label);
                  return (
                    <PerformanceChartTooltip
                      dataKeyFormatter={(l) =>
                        t(
                          `components.PerformanceChart.benchmarks.${visibleName(l)}`,
                          visibleName(l)
                        )
                      }
                      label={tooltipFormatter ? tooltipFormatter(visible) : visible}
                      payload={payload ? [...payload] : undefined}
                      valueFormatter={tooltipValueFormatter}
                      kpisDisabled={kpisDisabled}
                      disabledBenchmarks={kpisDisabled ? filteredBenchmarks : []}
                    />
                  );
                },
              }}
              activeDotProps={{ r: 2 }}
              {...lineChartProps}
              withLegend={false}
            />

            {/* External custom legend to avoid double-rendering and allow precise placement */}
            <Group justify="flex-end" style={{ paddingTop: 8, paddingRight: 28 }}>
              <CustomLegend
                payload={series.map((s) => ({
                  value: s.name,
                  color: (s as any).color,
                  payload: { payload: { label: (s as any).label ?? s.name } },
                }))}
              />
            </Group>
            {showLockerInLegend && (
              <ChoosePlanModal
                isOpen={showPlanModal}
                onClose={() => closePlanModal()}
                title={t("plans.changePlanAnytime")}
              />
            )}
          </>
        ) : (
          <EmptyState type="noData" {...props} text={t("common.noResults")} />
        )}
      </Stack>
    </Skeleton>
  );
};

Component.Tooltip = PerformanceChartTooltip;

export const PerformanceChart = Component;
